import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, ChevronUp } from 'lucide-react';

interface CompareProduct {
  id: string;
  name: string;
  image: string;
  price: number;
  compareAtPrice: number | null;
  rating: number;
  reviewCount: number;
  specifications: Record<string, string>;
}

interface ProductCompareProps {
  products: CompareProduct[];
  onRemove: (id: string) => void;
  onClose: () => void;
}

export function ProductCompare({ products = [], onRemove, onClose }: ProductCompareProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>('overview');

  if (!products || products.length === 0) return null;

  const allSpecKeys = new Set<string>();
  products.forEach((p) => {
    Object.keys(p.specifications).forEach((key) => allSpecKeys.add(key));
  });

  const specKeys = Array.from(allSpecKeys);

  const toggleSection = (section: string) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  const getSpecValue = (product: CompareProduct, key: string) => {
    return product.specifications[key] || '-';
  };

  const highlightDifferences = (key: string): boolean => {
    const values = products.map((p) => getSpecValue(p, key));
    return new Set(values).size > 1;
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-gray-900">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <h2 className="text-lg font-semibold">Compare Products ({products.length})</h2>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Close comparison"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-white dark:bg-gray-900 w-32 p-4 text-left text-sm font-medium text-muted-foreground border-b border-r border-gray-200 dark:border-gray-800">
                  Product
                </th>
                {products.map((product) => (
                  <th key={product.id} className="p-4 min-w-[180px] border-b border-gray-200 dark:border-gray-800">
                    <div className="relative">
                      <button
                        onClick={() => onRemove(product.id)}
                        className="absolute -top-1 -right-1 p-1 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-destructive hover:text-white transition-colors"
                        aria-label={`Remove ${product.name} from comparison`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-24 h-24 object-cover rounded-lg mx-auto mb-3"
                      />
                      <p className="text-sm font-medium text-center line-clamp-2">{product.name}</p>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              <tr className="bg-gray-50 dark:bg-gray-800/50">
                <td className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-800/50 p-3 text-sm font-medium border-b border-r border-gray-200 dark:border-gray-800">
                  Price
                </td>
                {products.map((product) => (
                  <td key={product.id} className="p-3 text-center border-b border-gray-200 dark:border-gray-800">
                    <span className="text-lg font-bold text-primary-600">
                      S${product.price.toFixed(2)}
                    </span>
                    {product.compareAtPrice && (
                      <span className="ml-2 text-sm text-muted-foreground line-through">
                        S${product.compareAtPrice.toFixed(2)}
                      </span>
                    )}
                  </td>
                ))}
              </tr>

              <tr className="bg-gray-50 dark:bg-gray-800/50">
                <td className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-800/50 p-3 text-sm font-medium border-b border-r border-gray-200 dark:border-gray-800">
                  Rating
                </td>
                {products.map((product) => (
                  <td key={product.id} className="p-3 text-center border-b border-gray-200 dark:border-gray-800">
                    <span className="text-sm font-medium">{product.rating}</span>
                    <span className="text-xs text-muted-foreground ml-1">
                      ({product.reviewCount})
                    </span>
                  </td>
                ))}
              </tr>

              {specKeys.map((key) => {
                const isDifferent = highlightDifferences(key);
                return (
                  <tr key={key} className={isDifferent ? 'bg-yellow-50/50 dark:bg-yellow-900/10' : ''}>
                    <td className="sticky left-0 z-10 bg-white dark:bg-gray-900 p-3 text-sm font-medium border-b border-r border-gray-200 dark:border-gray-800">
                      {key}
                    </td>
                    {products.map((product) => (
                      <td
                        key={product.id}
                        className={`p-3 text-center text-sm border-b border-gray-200 dark:border-gray-800 ${
                          isDifferent ? 'font-medium' : 'text-muted-foreground'
                        }`}
                      >
                        {getSpecValue(product, key)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="sticky bottom-0 px-4 py-3 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
        <p className="text-sm text-muted-foreground text-center">
          {products.length}/4 products selected. Add more or remove to compare.
        </p>
      </div>
    </div>
  );
}

interface CompareBarProps {
  products: CompareProduct[];
  onOpenCompare: () => void;
  onClear: () => void;
}

export function CompareBar({ products, onOpenCompare, onClear }: CompareBarProps) {
  if (products.length === 0) return null;

  return (
    <motion.div
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      exit={{ y: 100 }}
      className="fixed bottom-20 md:bottom-4 left-1/2 -translate-x-1/2 z-40 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-800 px-4 py-3 flex items-center gap-4"
    >
      <div className="flex -space-x-2">
        {products.map((product) => (
          <img
            key={product.id}
            src={product.image}
            alt={product.name}
            className="w-10 h-10 rounded-lg border-2 border-white dark:border-gray-900 object-cover"
          />
        ))}
      </div>
      <span className="text-sm font-medium">{products.length} products to compare</span>
      <button
        onClick={onOpenCompare}
        className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
      >
        Compare
      </button>
      <button
        onClick={onClear}
        className="p-2 text-muted-foreground hover:text-destructive transition-colors"
        aria-label="Clear comparison"
      >
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
}
